import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import ApolloError from '@shared/components/ApolloError';
import { MoveLicenseToIsacaValidator } from './validators/moveLicenseToIsacaValidator';
import { DialogActions, DialogContent } from '@mui/material';
import Asterisk from '@shared/components/Asterisk';
import MOVE_LICENSE_ORGANIZATIONS from '../../../queries/changeLicenseQueries/moveLicenseOrganizations';
import { MOVE_LICENSE_TYPES } from '../../../constants/MoveLicenseTypes';
import ConfirmDialogWithButton from '@shared/components/confirmDialog/ConfirmDialogWithButton';
import { DIALOG_MODES } from '@shared/components/confirmDialog/dialogWrappers/constants/DialogModes';
import { useNavigate } from 'react-router';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import MOVE_USER_LICENSE_TO_ORG from '../../../queries/changeLicenseQueries/moveUserLicenseToOrgMutation';
import { showErrorToast, showSuccessToast } from '@shared/services/toastService';
import FormOrgUnitSelector from '@shared/components/form/FormOrgUnitSelector';
import MotionSection from '@shared/components/animations/MotionSection';
import { AnimatePresence } from 'framer-motion';
import {
  CANCEL_TEXT,
  FORM_ID,
  FORM_INCOMPLETE_MESSAGE,
  MOVE_CONFIRMATION_MESSAGE,
  MOVE_TEXT,
  SAVE_TEXT,
  USER_MOVED_SUCCESSFULLY_MESSAGE
} from './constants/moveLicenseConstants';
import { LinkToast } from '../../../../notifications/toastNotification/components/LinkToast';
import { pageUrlService } from '@shared/services/pageUrlService';
import { VALIDATION_ERROR_CODES } from '../../../constants/ValidationErrorCodes';

const USER_APPRAISER_PROFILE_MESSAGE =
  'An Appraiser Profile must be created before moving this user.';
const METADATA_EVENT_ID_KEY = 'EventId';

/**
 * @typedef {object} MoveLicenseToIsacaProps
 * @property {React.ReactNode} [dialogContent]
 * @property {React.ReactNode} [dialogActions]
 * @property {number} [externalUserId]
 * @property {string} [previousLink]
 * @property {boolean} [isAppraiser]
 */

/**
 * @param {MoveLicenseToIsacaProps} props
 */
const MoveLicenseToIsaca = props => {
  const { dialogContent, dialogActions, externalUserId, previousLink, isAppraiser } = props;

  const form = useForm({
    mode: 'onChange',
    resolver: joiResolver(MoveLicenseToIsacaValidator.schema),
    defaultValues: {
      licensedOrgId: null,
      licensedOrgUnitId: null
    }
  });

  const { handleSubmit, setValue, trigger } = form;

  const navigate = useNavigate();
  const client = useApolloClient();

  const {
    data: popUpData,
    loading: popUpDataLoading,
    error: popUpDataError
  } = useQuery(MOVE_LICENSE_ORGANIZATIONS, {
    variables: {
      moveLicenseType: MOVE_LICENSE_TYPES.ISACA
    },
    onCompleted: data => {
      const [isacaOrg] = data.moveLicenseOrganizations;
      setValue('licensedOrgId', isacaOrg.Id);
    }
  });
  const organizations = popUpData?.moveLicenseOrganizations || [];

  const [moveUser, { error: moveUserError }] = useMutation(MOVE_USER_LICENSE_TO_ORG, {
    update: () => client.clearStore()
  });

  const handleConfirm = async formData => {
    if (!isAppraiser) {
      showErrorToast(USER_APPRAISER_PROFILE_MESSAGE);
      return;
    }

    const result = await moveUser({
      variables: {
        userLicenseInfo: {
          ExternalUserId: externalUserId,
          LicensedOrgId: formData.licensedOrgId,
          LicensedOrgUnitId: formData.licensedOrgUnitId,
          SystemRoleId: formData.systemRoleId
        }
      }
    });

    if (!result.data.moveUserLicenseToOrg.Success) {
      result.data.moveUserLicenseToOrg.Errors.forEach(err => {
        if (
          err.Code == VALIDATION_ERROR_CODES.FUTURE_ASSIGNED_EVENTS ||
          err.Code == VALIDATION_ERROR_CODES.FUTURE_INVITED_EVENTS
        ) {
          showErrorToast(
            <LinkToast
              message={err.Message}
              url={pageUrlService.getEditEventPageUrl(
                err.Metadata.find(x => x.Key === METADATA_EVENT_ID_KEY).Value
              )}
              openInNewTab={true}
            />
          );

          return;
        }

        showErrorToast(err.Message);
      });

      return;
    }

    showSuccessToast(USER_MOVED_SUCCESSFULLY_MESSAGE);
    navigate(previousLink);
  };

  const openModalPreCondition = async () => {
    const validationResult = await trigger();

    if (!validationResult) {
      showErrorToast(FORM_INCOMPLETE_MESSAGE);
    }

    return validationResult;
  };

  if (popUpDataError || moveUserError) {
    return <ApolloError />;
  }

  return (
    <form id={FORM_ID} onSubmit={handleSubmit(handleConfirm)}>
      <FormProvider {...form}>
        <DialogContent>
          {dialogContent}

          <AnimatePresence>
            <MotionSection keyProp='move-license-to-isaca'>
              <div className='page-section'>
                <h4 className='field-title'>
                  Unit
                  <Asterisk />
                </h4>

                <FormOrgUnitSelector
                  organizations={organizations}
                  loading={popUpDataLoading}
                  multiSelect={false}
                  name='licensedOrgUnitId'
                />
              </div>
            </MotionSection>
          </AnimatePresence>
        </DialogContent>

        <DialogActions>
          {dialogActions}

          <ConfirmDialogWithButton
            openModalBtnText={SAVE_TEXT}
            confirmBtnText={MOVE_TEXT}
            cancelBtnText={CANCEL_TEXT}
            message={MOVE_CONFIRMATION_MESSAGE}
            dialogMode={DIALOG_MODES.EXTERNAL_FORM_SUBMIT}
            formId={FORM_ID}
            openModalPreCondition={openModalPreCondition}
          />
        </DialogActions>
      </FormProvider>
    </form>
  );
};

export default MoveLicenseToIsaca;
